import axios from "axios";
import { signOut } from "firebase/auth";

import { auth } from "./firebase";
import { clearAuthCache } from "./authUtils";
import { clearActivityTimestamp } from "../hooks/useIdleTimer";

/**
 * Stable server-issued codes that mean "this session is over, do not retry".
 *
 * Both backends emit these alongside their human-readable message: the API as a
 * `code` field on the 403 body, DashAI's Cloud Functions the same. Matching the
 * code rather than the prose is the whole point — the message is copy, and will
 * be reworded or localised, so a client that greps English has a contract that
 * breaks silently.
 */
export const TERMINAL_AUTH_CODES = new Set(["account_deactivated"]);

/** Message shown when the server sends no usable prose. */
const FALLBACK_MESSAGE = "Your access has been revoked. Contact your administrator.";

let lastReason = null;

/**
 * Take the reason the session was terminated, clearing it.
 *
 * Read-once so a reason cannot outlive the sign-out that produced it and
 * reappear on an unrelated visit to the login screen. The interceptor captures
 * it because only the response knows why — the server's prose is the only
 * explanation available, and it is gone by the time any screen mounts.
 *
 * Consumed by `pages/auth/LoginPage.jsx`, which holds the result in a ref: the
 * take clears the stored value, so a caller that reads it twice on one mount
 * gets the message on the first read and nothing on the second.
 */
export function takeTerminalAuthReason() {
  const reason = lastReason;
  lastReason = null;
  return reason;
}

/**
 * Decide whether a response is a terminal authorization refusal.
 *
 * Deliberately narrow: an ordinary 403 (no workspace access, insufficient role)
 * must NOT end the session — a user denied one workspace is still legitimately
 * signed in, and logging them out would be a far worse bug than the one this
 * closes. Only an explicit code qualifies.
 */
export function isTerminalAuthError(status, body) {
  if (status !== 403) return false;
  const code = body?.code;
  return typeof code === "string" && TERMINAL_AUTH_CODES.has(code);
}

/**
 * End the session because the server said this identity is finished.
 *
 * `signingOut` collapses refusals that arrive while a sign-out is *in flight* —
 * the common case, since a page fires several requests at once. It is not
 * general idempotency: calls that arrive after the flag resets each sign out
 * again. That is harmless (signing out an already-signed-out session is a
 * no-op) and simpler than tracking completion, which would need invalidating
 * whenever the user signs back in.
 */
let signingOut = false;
let storeRef = null;

/**
 * Whether the caller is currently impersonating someone.
 *
 * Lazy dynamic import with a cached ref, mirroring `api/authApi.ts` — importing
 * the store eagerly from a module the store's own slices reach would be a cycle.
 */
async function isImpersonating() {
  try {
    if (!storeRef) {
      const mod = await import("../store/store");
      // Named export — `store/store.js` has no default.
      storeRef = mod.store;
    }
    return Boolean(storeRef.getState()?.auth?.isImpersonating);
  } catch {
    // Store unavailable (very early boot): treat as not impersonating, which
    // keeps the sign-out behaviour rather than silently disabling it.
    return false;
  }
}

export async function handleTerminalAuthError(body) {
  // An admin may impersonate a deactivated user, and during impersonation the
  // token's identity is the TARGET's — so every request legitimately refuses
  // with this code. Signing out here would strand the admin as that user,
  // unable to reach the restore endpoint that exists precisely to prevent it.
  // The refusals still surface to the caller; only the sign-out is skipped.
  if (await isImpersonating()) {
    return;
  }

  // Assigned only once the sign-out is going ahead. Staging it earlier left it
  // briefly set while the impersonation check awaited, so a concurrent reader
  // could see a reason for a session that was never ended.
  lastReason = body?.detail || body?.error || FALLBACK_MESSAGE;

  if (signingOut) return;
  signingOut = true;
  try {
    await signOut(auth);
    clearAuthCache();
    clearActivityTimestamp();
  } catch (err) {
    // Signing out is best-effort: the account is already refused server-side,
    // so a failure here cannot grant access. Log it and leave the reason set
    // so the UI can still explain what happened.
    console.error("Failed to sign out after a terminal auth error:", err);
  } finally {
    signingOut = false;
  }
}

/**
 * Register the response interceptor on the default axios instance.
 *
 * One choke point instead of per-call handling: the app has three
 * `makeAuthenticatedRequest` implementations and dozens of call sites, and a
 * deactivated user's next request could be any of them. Handling this at each
 * site is how it would get missed.
 *
 * Coverage note — two gaps, both leaving the session alive rather than granting
 * anything:
 *
 *  - The axios transport only. 65 modules call `fetch` directly,
 *    including all of `features/dashai/`; they surface the server's refusal as
 *    an error but do not end the session. `isTerminalAuthError` +
 *    `handleTerminalAuthError` are exported so a fetch path can adopt this.
 *  - `responseType: "blob"` requests (the two export downloads) deliver an
 *    error body as a Blob, so the code is not readable without parsing it
 *    asynchronously. A refusal there is missed; the next ordinary request ends
 *    the session instead.
 */
export function installTerminalAuthInterceptor() {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const body = error?.response?.data;
      if (isTerminalAuthError(status, body)) {
        // Fire and forget: the rejection below still propagates so the caller's
        // own error handling runs, and the sign-out lands independently.
        void handleTerminalAuthError(body);
      }
      return Promise.reject(error);
    },
  );
}
